import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { LandingLayout } from '../src/layouts/LandingLayout';
import { Link } from '../src/components/primitives/Link';

const H3: FC = ({ children }) => (
  <Typography
    color="text.primary"
    variant="h3"
    sx={{
      xs: 4,
      lg: 6,
      ['+h3']: {
        mt: 3,
      }
    }}
  >
    {children}
  </Typography>
)

const Paragraph: FC = ({ children }) => (
  <Box
    color="text.secondary"
    sx={{
      textShadow: '#000 0 0 5px, #000 0 0 10px',
    }}
  >
    {children}
  </Box>
)

export default function PrivacyPolicy() {
  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 4, lg: 8 },
      px: { xs: 4, lg: 0 },
      pt: { xs: 8, lg: 12 },
      pb: { xs: 12, lg: 24 }
    }}>
      <Typography variant="h1" sx={{ mb: 2 }}>Privacy Policy for YLDR</Typography>
      <Paragraph>
        <H3>Introduction</H3>
        <Typography variant="secondary14">
          Welcome to YLDR (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you visit our website{' '}
          <Link href="https://yldr.com/">https://yldr.com/ </Link> (the &quot;Site&quot;). Please read this Privacy
          Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Site.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Information We Collect</H3>
        <Typography variant="secondary14">
          We may collect personal information from you when you interact with our Site. This information may include:
        </Typography>
        <ol>
          <li>Personal identification information, such as your name, email address, and contact information.</li>
          <li>Transaction information related to your use of our services.</li>
          <li>
            Technical information automatically collected when you visit our Site, such as your IP address, browser
            type, and operating system.
          </li>
        </ol>
      </Paragraph>
      <Paragraph>
        <H3>How We Use Your Information</H3>
        <Typography variant="secondary14">
          We may use the information we collect from you to:
        </Typography>
        <ol>
          <li>Provide and maintain our services.</li>
          <li>Improve our services and develop new features.</li>
          <li>Communicate with you, including responding to your inquiries and providing customer support.</li>
          <li>Comply with legal obligations and enforce our terms and policies.</li>
        </ol>
      </Paragraph>
      <Paragraph>
        <H3>Disclosure of Your Information</H3>
        <Typography variant="secondary14">
          We may disclose your information to third parties in the following circumstances:
        </Typography>
        <ol>
          <li>With your consent.</li>
          <li>To comply with legal obligations or respond to lawful requests from governmental authorities.</li>
          <li>To protect the rights, property, or safety of YLDR, our users, or others.</li>
        </ol>
      </Paragraph>
      <Paragraph>
        <H3>Data Security</H3>
        <Typography variant="secondary14">
          We take reasonable measures to protect the confidentiality and security of your information. However, please
          be aware that no method of transmission over the Internet or electronic storage is completely secure, and we
          cannot guarantee absolute security.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Third-Party Links</H3>
        <Typography variant="secondary14">
          Our Site may contain links to third-party websites or services that are not owned or controlled by YLDR. We
          are not responsible for the privacy practices or content of these third-party sites. We encourage you to
          review the privacy policies of any third-party sites you visit.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Children&lsquo;s Privacy</H3>
        <Typography variant="secondary14">
          Our services are not intended for individuals under the age of 18. We do not knowingly collect personal
          information from children under 18. If you are a parent or guardian and believe that your child has provided
          us with personal information, please contact us so that we can delete their information.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Changes to This Privacy Policy</H3>
        <Typography variant="secondary14">
          We may update our Privacy Policy from time to time. Any changes we make will be posted on this page, with an
          updated &quot;Last Updated&quot; date at the top of the page. We encourage you to review this Privacy Policy
          periodically for any changes.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Contact Us</H3>
        <Typography variant="secondary14">
          If you have any questions or concerns about our Privacy Policy, please contact us at{' '}
          <Link href="mailto:admin@yldr.com">admin@yldr.com</Link>.
        </Typography>
      </Paragraph>
    </Box>
  );
}

PrivacyPolicy.getLayout = function getLayout(page: React.ReactElement) {
  return <LandingLayout>{page}</LandingLayout>;
};
